import { zipStore } from "./zip";

// A minimal WordprocessingML package: title, meta line, overview and one
// numbered block per section, all as direct formatting (no styles part), so
// Word, LibreOffice and a Google Drive upload render it the same way.

const DOCX_TYPE = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

function escapeXml(value) {
    return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

// `size` is in half-points (22 = 11pt); `color` a hex triplet.
function run(text, { bold = false, size, color } = {}) {
    const props = [
        bold ? "<w:b/>" : "",
        size ? `<w:sz w:val="${size}"/><w:szCs w:val="${size}"/>` : "",
        color ? `<w:color w:val="${color}"/>` : "",
    ].join("");

    const lines = String(text ?? "")
        .split("\n")
        .map((line) => `<w:t xml:space="preserve">${escapeXml(line)}</w:t>`)
        .join("<w:br/>");

    return `<w:r>${props ? `<w:rPr>${props}</w:rPr>` : ""}${lines}</w:r>`;
}

// Spacing in twentieths of a point (120 = 6pt).
function paragraph(runs, { before = 0, after = 120 } = {}) {
    return `<w:p><w:pPr><w:spacing w:before="${before}" w:after="${after}"/></w:pPr>${runs.join("")}</w:p>`;
}

/**
 * @param {object} script  { title, overview, blocks[{ title, minutes, script }] }
 * @param {{ subtitle?: string, footer?: string, minutes: (count: number) => string }} options
 * @returns {Blob} the .docx file
 */
export function buildScriptDocx(script, { subtitle = "", footer = "", minutes }) {
    const body = [
        paragraph([run(script.title, { bold: true, size: 40, color: "141414" })], { after: 60 }),
        subtitle ? paragraph([run(subtitle, { size: 20, color: "888888" })], { after: 240 }) : "",
        paragraph([run(script.overview, { size: 23, color: "444444" })], { after: 360 }),
        ...script.blocks.flatMap((block, index) => [
            paragraph([run(`${String(index + 1).padStart(2, "0")}   ·   ${minutes(block.minutes)}`, { bold: true, size: 18, color: "999999" })], { before: 240, after: 40 }),
            paragraph([run(block.title, { bold: true, size: 26, color: "141414" })], { after: 80 }),
            paragraph([run(block.script, { size: 22, color: "2b2b2b" })], { after: 200 }),
        ]),
        footer ? paragraph([run(footer, { size: 18, color: "999999" })], { before: 480 }) : "",
    ].join("");

    const document = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:body>${body}<w:sectPr><w:pgSz w:w="11906" w:h="16838"/><w:pgMar w:top="1417" w:right="1417" w:bottom="1417" w:left="1417" w:header="708" w:footer="708" w:gutter="0"/></w:sectPr></w:body></w:document>`;

    const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/></Types>`;

    const packageRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/></Relationships>`;

    const documentRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"></Relationships>`;

    return zipStore([
        { name: "[Content_Types].xml", data: contentTypes },
        { name: "_rels/.rels", data: packageRels },
        { name: "word/document.xml", data: document },
        { name: "word/_rels/document.xml.rels", data: documentRels },
    ], DOCX_TYPE);
}
